/** Cienki klient API. Sesja jedzie w ciasteczku httpOnly, więc tylko credentials: 'include'. */

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** Godzina zakładowa z ostatniej odpowiedzi — do znacznika "dane na HH:MM". */
export let lastPlantTime: string | null = null;

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  lastPlantTime = res.headers.get('X-Plant-Time') ?? lastPlantTime;

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError((data as any).error ?? `Błąd ${res.status}`, res.status);
  return data as T;
}

export const api = {
  get: <T,>(p: string) => call<T>(p),
  post: <T,>(p: string, body?: unknown) =>
    call<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T,>(p: string, body: unknown) =>
    call<T>(p, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T,>(p: string) => call<T>(p, { method: 'DELETE' }),
};

// ---------------------------------------------------------------- typy

/** Pracownika identyfikuje wyłącznie login — aplikacja nie trzyma imion ani nazwisk. */
export interface User {
  id: number;
  login: string;
  role: 'worker' | 'leader' | 'admin';
  station_code: string | null;
}

export interface Station {
  code: string;
  name: string;
  sort_order: number;
  is_parked: number;
  /** Stanowisko nadrzędne — Sniffer i Wytrzymałość podlegają pod Kontrolę jakości. */
  parent_code: string | null;
}

export interface Operation {
  code: string;
  name: string;
  /** Stanowisko wiodące — do opisu w marszrucie i raportach. */
  station_code: string;
  /** Wszystkie kafle, z których operacja jest dostępna (freon: każdy). */
  stations: string[];
  is_global: number;
  sort_order: number;
}

export interface OrderSummary {
  zp_number: string;
  product_code: string | null;
  product_name: string | null;
  customer: string | null;
  qty: number;
}

export interface SerialState {
  serial_no: string;
  product_code: string | null;
  position: number | null;
  workedBy: string[];
  isFinished: boolean;
  lastDate: string | null;
}

export interface OrderDetail {
  order: OrderSummary;
  /** Normy wszystkich operacji tego wyrobu: kod operacji -> godziny na sztukę. */
  normy: Record<string, number>;
  standardHours: number | null;
  serials: SerialState[];
  startedCount: number;
  totalCount: number;
}

export interface DaySummary {
  employee_id: number;
  work_date: string;
  registeredMin: number;
  availableMin: number;
  creditMin: number;
  gapMin: number;
  efficiency: number | null;
  latestEndMin: number | null;
  entryCount: number;
  missingStandard: string[];
}

export interface DayEntry {
  id: number;
  work_date: string;
  station_code: string;
  operations: string[];
  zp_number: string | null;
  start_min: number;
  end_min: number;
  is_prototype: number;
  is_finished: number;
  is_backdated: number;
  notes: string | null;
  serials: string[];
  workedMin: number;
  creditMin: number;
}

export interface DayReport {
  date: string;
  month: string;
  week: number;
  summary: DaySummary;
  entries: DayEntry[];
  plantTime: string;
}

export interface EfficiencyReport {
  from: string;
  to: string;
  employees: Array<{
    employee: { id: number; login: string };
    total: { creditMin: number; availableMin: number; registeredMin: number; gapMin: number; efficiency: number | null; days: number };
    days: DaySummary[];
  }>;
  overall: { creditMin: number; availableMin: number; registeredMin: number; gapMin: number; efficiency: number | null; days: number };
}

export interface Employee {
  id: number;
  login: string;
  employee_code: string | null;
  zone: string | null;
  station_code: string | null;
  role: 'worker' | 'leader' | 'admin';
  is_active: number;
  has_pin: number;
  location: string;
}
